import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import SessionDiagnosticsCard from '../../components/admin/SessionDiagnosticsCard';
import { useDashboardCustomizer } from '../../components/admin/DashboardCustomizer';
import {
  Shield, Lock, Key, AlertTriangle, CheckCircle, Activity,
  Smartphone, Clock, RefreshCw, Loader2, Eye, Download,
  ChevronDown, ChevronUp, Globe, Zap, ShieldAlert, ShieldCheck,
  Fingerprint, Bell, Plus, Trash2, Settings, ToggleLeft, ToggleRight, X,
  Target, Users, XCircle, Info, TrendingUp, Phone, Send, Unlock,
  ShieldOff, History, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line
} from 'recharts';
import { apiClient } from '../../api/client';

const BRAND = '#F5831F';
const DANGER = '#DC2626';
const SUCCESS = '#16A34A';
const INFO = '#2563EB';
const MUTED = '#44403A';

const GEO_NAMES = {
  RU: 'Russia', IN: 'India', PK: 'Pakistan', BD: 'Bangladesh', CN: 'China',
  NG: 'Nigeria', VN: 'Vietnam', ID: 'Indonesia', IR: 'Iran', KP: 'North Korea',
  BY: 'Belarus', UA: 'Ukraine', PH: 'Philippines', BR: 'Brazil', TR: 'Turkey',
};

const MetricCard = ({ icon: Icon, label, value, sub, color = BRAND, accent }) => (
  <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid={`sec-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium" style={{ color: MUTED }}>{label}</p>
        <p className="text-3xl font-bold mt-1" style={{ color: accent || '#1A1209' }}>{value}</p>
        {sub && <p className="text-xs mt-1" style={{ color: MUTED }}>{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} style={{ color }} />
      </div>
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, right }) => (
  <div className="flex items-center justify-between mb-4 mt-8 first:mt-0">
    <div className="flex items-center gap-2">
      <Icon size={18} style={{ color: BRAND }} />
      <h2 className="text-base font-bold" style={{ color: '#1A1209' }}>{title}</h2>
    </div>
    {right}
  </div>
);

const SECURITY_SECTIONS = [
  { id: 'score', label: 'Security Score', order: 1 },
  { id: 'compliance', label: 'SOC2 Compliance', order: 2 },
  { id: 'threat', label: 'Threat Intelligence', order: 3 },
  { id: 'sessions', label: 'Active Sessions', order: 4 },
  { id: 'anomalies', label: 'Anomaly Detection', order: 5 },
  { id: 'recommendations', label: 'Recommendations', order: 6 },
  { id: 'overview', label: 'Overview Metrics', order: 7 },
  { id: 'comparison', label: 'Period Comparison', order: 8 },
  { id: 'heatmap', label: 'Attack Heatmap', order: 9 },
  { id: 'trend', label: 'Login Trend', order: 10 },
  { id: '2fa', label: '2FA Adoption', order: 11 },
  { id: 'events', label: 'Event Breakdown', order: 12 },
  { id: 'flagged', label: 'Flagged IPs', order: 13 },
  { id: 'blocked', label: 'Blocked IPs', order: 14 },
  { id: 'whitelist', label: 'IP Whitelist', order: 15 },
  { id: 'rotation', label: 'Key Rotation', order: 16 },
  { id: 'ratelimits', label: 'Rate Limits', order: 17 },
  { id: 'recentevents', label: 'Recent Events', order: 18 },
];

const AdminSecurity = () => {
  const { sectionProps, CustomizerPanel, CustomizerButton } = useDashboardCustomizer('security', SECURITY_SECTIONS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [rotateConfirm, setRotateConfirm] = useState(false);
  const [blockingIp, setBlockingIp] = useState(null);
  const [unblockingIp, setUnblockingIp] = useState(null);
  const [manualBlockIp, setManualBlockIp] = useState('');
  const [showAutoConfig, setShowAutoConfig] = useState(false);
  const [autoConfig, setAutoConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [whitelistIp, setWhitelistIp] = useState('');
  const [whitelistLabel, setWhitelistLabel] = useState('');
  const [addingWhitelist, setAddingWhitelist] = useState(false);
  const [removingWl, setRemovingWl] = useState(null);
  const [showNotifs, setShowNotifs] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [userHealth, setUserHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [showDigest, setShowDigest] = useState(false);
  const [digestHtml, setDigestHtml] = useState('');
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestSent, setDigestSent] = useState(false);
  const [showUserHealth, setShowUserHealth] = useState(false);
  const [showAlertConfig, setShowAlertConfig] = useState(false);
  const [alertRecipients, setAlertRecipients] = useState('');
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertTypes, setAlertTypes] = useState(['auto_block', 'brute_force', 'off_hours_admin']);
  const [savingAlert, setSavingAlert] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);
  const [bulkBlocking, setBulkBlocking] = useState(false);
  const [unlockingUser, setUnlockingUser] = useState(null);
  const [geoBlock, setGeoBlock] = useState({ enabled: true, countries: [] });
  const [geoBlockInput, setGeoBlockInput] = useState('');
  const [geoBlockSaving, setGeoBlockSaving] = useState(false);

  const loadGeoBlock = useCallback(async () => {
    try {
      const r = await apiClient.request('/api/admin/security/geo-block');
      setGeoBlock(r);
    } catch (e) { console.error('Failed to load geo-block config:', e); }
  }, []);

  useEffect(() => { loadGeoBlock(); }, [loadGeoBlock]);

  const saveGeoBlock = async (payload) => {
    setGeoBlockSaving(true);
    try {
      const r = await apiClient.request('/api/admin/security/geo-block', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setGeoBlock(r);
    } catch (e) { console.error('Geo-block update failed:', e); }
    setGeoBlockSaving(false);
  };

  const handleGeoBlockToggle = () => saveGeoBlock({ enabled: !geoBlock.enabled });
  const handleGeoBlockAdd = () => {
    const code = geoBlockInput.trim().toUpperCase();
    if (code.length !== 2) return;
    saveGeoBlock({ countries: [...(geoBlock.countries || []), code] });
    setGeoBlockInput('');
  };
  const handleGeoBlockRemove = (code) => saveGeoBlock({ countries: (geoBlock.countries || []).filter(c => c !== code) });

  const load = useCallback(async () => {
    try {
      const result = await apiClient.request('/api/admin/security/dashboard');
      setData(result);
      if (!autoConfig) setAutoConfig(result.autoblock_config);
    } catch (e) {
      console.error('Failed to load security dashboard:', e);
    }
    setLoading(false);
  }, [autoConfig]);

  useEffect(() => { load(); }, [load]);

  const handleRotateKey = async () => {
    setRotating(true);
    try {
      await apiClient.request('/api/admin/security/rotate-key', { method: 'POST' });
      await load();
      setRotateConfirm(false);
    } catch (e) { console.error('Key rotation failed:', e); }
    setRotating(false);
  };

  const handleBlockIp = async (ip, reason) => {
    setBlockingIp(ip);
    try {
      await apiClient.request('/api/admin/security/block-ip', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip, reason: reason || 'Blocked from Security Dashboard' }),
      });
      await load();
    } catch (e) { console.error('Block IP failed:', e); }
    setBlockingIp(null);
  };

  const handleUnblockIp = async (ip) => {
    setUnblockingIp(ip);
    try {
      await apiClient.request('/api/admin/security/unblock-ip', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip }),
      });
      await load();
    } catch (e) { console.error('Unblock IP failed:', e); }
    setUnblockingIp(null);
  };

  const handleSaveAutoConfig = async () => {
    setSavingConfig(true);
    try {
      await apiClient.request('/api/admin/security/autoblock-config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(autoConfig),
      });
      await load();
    } catch (e) { console.error('Config save failed:', e); }
    setSavingConfig(false);
  };

  const handleAddWhitelist = async () => {
    if (!whitelistIp.trim()) return;
    setAddingWhitelist(true);
    try {
      await apiClient.request('/api/admin/security/whitelist', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: whitelistIp.trim(), label: whitelistLabel.trim() }),
      });
      setWhitelistIp('');
      setWhitelistLabel('');
      await load();
    } catch (e) { console.error('Add whitelist failed:', e); }
    setAddingWhitelist(false);
  };

  const handleRemoveWhitelist = async (ip) => {
    setRemovingWl(ip);
    try {
      await apiClient.request(`/api/admin/security/whitelist/${ip}`, { method: 'DELETE' });
      await load();
    } catch (e) { console.error('Remove whitelist failed:', e); }
    setRemovingWl(null);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/security/export-csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('hibff_auth_token')}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security_report_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed:', e); }
    setExporting(false);
  };

  const loadUserHealth = async () => {
    setLoadingHealth(true);
    try {
      const result = await apiClient.request('/api/admin/security/user-health');
      setUserHealth(result);
    } catch (e) { console.error('User health failed:', e); }
    setLoadingHealth(false);
  };

  const loadDigestPreview = async () => {
    try {
      const result = await apiClient.request('/api/admin/security/digest-preview');
      setDigestHtml(result.html || '');
    } catch (e) { console.error('Digest preview failed:', e); }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      await apiClient.request('/api/admin/security/send-digest', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      setDigestSent(true);
      setTimeout(() => setDigestSent(false), 5000);
    } catch (e) { console.error('Send digest failed:', e); }
    setSendingDigest(false);
  };

  const handleSaveAlertConfig = async () => {
    setSavingAlert(true);
    try {
      const phones = alertRecipients.split(',').map(p => p.trim()).filter(Boolean);
      await apiClient.request('/api/admin/security/alert-config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: alertEnabled, recipients: phones, alert_types: alertTypes }),
      });
      load();
    } catch (e) { console.error('Save alert config failed:', e); }
    setSavingAlert(false);
  };

  const handleTestAlert = async () => {
    setTestingAlert(true);
    try {
      await apiClient.request('/api/admin/security/test-alert', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: testPhone, score: security_score?.score }),
      });
    } catch (e) { console.error('Test alert failed:', e); }
    setTestingAlert(false);
  };

  const handleBulkBlock = async () => {
    if (!window.confirm('Block ALL flagged IPs with 5+ failed attempts? This action will be logged.')) return;
    setBulkBlocking(true);
    try {
      const result = await apiClient.request('/api/admin/security/bulk-block-flagged', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ min_attempts: 5 }),
      });
      alert(`Blocked ${result.blocked} IPs`);
      load();
    } catch (e) { console.error('Bulk block failed:', e); }
    setBulkBlocking(false);
  };

  const handleUnlockAccount = async (userId) => {
    setUnlockingUser(userId);
    try {
      await apiClient.request('/api/admin/security/unlock-account', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      load();
    } catch (e) { console.error('Unlock failed:', e); }
    setUnlockingUser(null);
  };

  // Initialize alert config from data
  useEffect(() => {
    if (data?.sms_alert_config && Object.keys(data.sms_alert_config).length > 0) {
      setAlertEnabled(data.sms_alert_config.enabled || false);
      setAlertRecipients((data.sms_alert_config.recipients || []).join(', '));
      setAlertTypes(data.sms_alert_config.alert_types || ['auto_block', 'brute_force', 'off_hours_admin']);
    }
  }, [data]);

  if (loading) {
    return (
      <AdminLayout pageTitle="Security">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin" size={32} style={{ color: BRAND }} />
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout pageTitle="Security">
        <div className="text-center py-20">
          <ShieldAlert size={48} className="mx-auto mb-3" style={{ color: DANGER }} />
          <p className="text-sm" style={{ color: MUTED }}>Failed to load security data</p>
          <button onClick={() => { setLoading(true); load(); }} className="mt-3 px-4 py-2 rounded-lg text-sm text-white" style={{ backgroundColor: BRAND }}>Retry</button>
        </div>
      </AdminLayout>
    );
  }

  const { overview, login_trend, flagged_ips, blocked_ips, whitelisted_ips, notifications, two_fa_adoption, trusted_devices, rate_limits, key_rotation, recent_events, event_breakdown, security_score, compliance, threat_intel, active_sessions, anomalies, recommendations, heatmap, period_comparison, locked_accounts, sms_alert_config, timeline } = data;
  const blockedSet = new Set((blocked_ips || []).map(b => b.ip));
  const whitelistSet = new Set((whitelisted_ips || []).map(w => w.ip));
  const unreadNotifs = (notifications || []).length;

  const twoFaPieData = [
    { name: 'SMS 2FA', value: two_fa_adoption.sms_2fa },
    { name: 'Email 2FA', value: Math.max(two_fa_adoption.email_2fa - two_fa_adoption.sms_2fa, 0) },
    { name: 'No 2FA', value: Math.max(two_fa_adoption.total_users - two_fa_adoption.any_2fa, 0) },
  ].filter(d => d.value > 0);

  const severityColor = (action) => {
    if (action.includes('failed') || action.includes('blocked')) return DANGER;
    if (action.includes('rotated') || action.includes('changed') || action.includes('reset')) return '#F59E0B';
    return SUCCESS;
  };
  const formatAction = (a) => a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <AdminLayout pageTitle="Security">
      {/* ── Top Action Bar ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {/* Notifications bell */}
          <button
            onClick={() => setShowNotifs(!showNotifs)}
            className="relative p-2 rounded-lg transition-colors"
            style={{ backgroundColor: showNotifs ? `${BRAND}15` : '#F5F5F5' }}
            data-testid="notif-bell-btn"
          >
            <Bell size={18} style={{ color: unreadNotifs > 0 ? DANGER : MUTED }} />
            {unreadNotifs > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ backgroundColor: DANGER }}>
                {unreadNotifs}
              </span>
            )}
          </button>
          {autoConfig && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: autoConfig.enabled ? `${SUCCESS}12` : '#F5F5F5', color: autoConfig.enabled ? SUCCESS : MUTED }}>
              {autoConfig.enabled ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
              Auto-block {autoConfig.enabled ? 'ON' : 'OFF'}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setShowDigest(!showDigest); if (!digestHtml) loadDigestPreview(); }} className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5" style={{ backgroundColor: showDigest ? `${INFO}15` : '#F5F5F5', color: showDigest ? INFO : MUTED }} data-testid="digest-btn">
            <Activity size={14} /> Digest
          </button>
          <button onClick={() => { setShowUserHealth(!showUserHealth); if (!userHealth) loadUserHealth(); }} className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5" style={{ backgroundColor: showUserHealth ? `${DANGER}12` : '#F5F5F5', color: showUserHealth ? DANGER : MUTED }} data-testid="user-health-btn">
            <Users size={14} /> Health
          </button>
          <button onClick={() => setShowAlertConfig(!showAlertConfig)} className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5" style={{ backgroundColor: showAlertConfig ? `${SUCCESS}15` : '#F5F5F5', color: showAlertConfig ? SUCCESS : MUTED }} data-testid="sms-alert-btn">
            <Phone size={14} /> SMS Alerts
          </button>
          <button onClick={() => setShowTimeline(!showTimeline)} className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5" style={{ backgroundColor: showTimeline ? `${BRAND}15` : '#F5F5F5', color: showTimeline ? BRAND : MUTED }} data-testid="timeline-btn">
            <History size={14} /> Timeline
          </button>
          <button onClick={() => setShowAutoConfig(!showAutoConfig)} className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5" style={{ backgroundColor: '#F5F5F5', color: MUTED }} data-testid="autoblock-config-btn">
            <Settings size={14} /> Auto-Block
          </button>
          <CustomizerButton />
          <button onClick={handleExportCSV} disabled={exporting} className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 text-white" style={{ backgroundColor: BRAND }} data-testid="export-csv-btn">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export
          </button>
        </div>
      </div>

      {/* ── CheatSheet + Layout Customizer ── */}
      <CheatSheet pageId="security" />
      <CustomizerPanel />

      {/* Sprint 182 — Session Diagnostics. Live auth-channel snapshot so
          Robert can verify session health in one glance, instead of
          discovering Command Centre is empty by browsing tabs. */}
      <div className="mb-6">
        <SessionDiagnosticsCard />
      </div>

      {/* ── Notifications Dropdown ── */}
      {showNotifs && (
        <div className="bg-white rounded-lg mb-6 overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
            <p className="text-sm font-medium" style={{ color: '#1A1209' }}>Security Notifications</p>
            <button onClick={() => setShowNotifs(false)}><X size={14} style={{ color: MUTED }} /></button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {(notifications || []).length > 0 ? notifications.map((n, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 border-b" style={{ borderColor: '#F5F5F5' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${DANGER}12` }}>
                  <ShieldAlert size={12} style={{ color: DANGER }} />
                </div>
                <div>
                  <p className="text-xs font-medium" style={{ color: '#1A1209' }}>
                    Auto-blocked <span className="font-mono">{n.ip}</span>
                  </p>
                  <p className="text-[10px]" style={{ color: MUTED }}>{n.reason}</p>
                  <p className="text-[10px]" style={{ color: MUTED }}>
                    {n.timestamp && new Date(n.timestamp).toLocaleString()}
                    {n.expires_at && ` — Expires: ${new Date(n.expires_at).toLocaleString()}`}
                  </p>
                </div>
              </div>
            )) : (
              <p className="text-sm text-center py-6" style={{ color: MUTED }}>No notifications</p>
            )}
          </div>
        </div>
      )}

      {/* ── Auto-Block Config Panel ── */}
      {showAutoConfig && autoConfig && (
        <div className="bg-white rounded-lg mb-6 p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="autoblock-config-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Auto-Block Rules</h3>
            <button onClick={() => setAutoConfig(prev => ({ ...prev, enabled: !prev.enabled }))} data-testid="autoblock-toggle">
              {autoConfig.enabled
                ? <ToggleRight size={28} style={{ color: SUCCESS }} />
                : <ToggleLeft size={28} style={{ color: MUTED }} />}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: MUTED }}>Failure Threshold</label>
              <input type="number" min="3" max="100" value={autoConfig.threshold} onChange={e => setAutoConfig(prev => ({ ...prev, threshold: parseInt(e.target.value) || 20 }))}
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="threshold-input" />
              <p className="text-[10px] mt-1" style={{ color: MUTED }}>Failed logins before block</p>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: MUTED }}>Window (minutes)</label>
              <input type="number" min="1" max="1440" value={autoConfig.window_minutes} onChange={e => setAutoConfig(prev => ({ ...prev, window_minutes: parseInt(e.target.value) || 10 }))}
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="window-input" />
              <p className="text-[10px] mt-1" style={{ color: MUTED }}>Time period to count failures</p>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: MUTED }}>Block Duration (hours)</label>
              <input type="number" min="0" max="8760" value={autoConfig.block_duration_hours} onChange={e => setAutoConfig(prev => ({ ...prev, block_duration_hours: parseInt(e.target.value) || 0 }))}
                className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="duration-input" />
              <p className="text-[10px] mt-1" style={{ color: MUTED }}>0 = permanent block</p>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={handleSaveAutoConfig} disabled={savingConfig} className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5" style={{ backgroundColor: BRAND }} data-testid="save-autoblock-btn">
              {savingConfig ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              Save Configuration
            </button>
          </div>
        </div>
      )}

      {/* ── Weekly Digest Panel ── */}
      {showDigest && (
        <div className="bg-white rounded-lg mb-6 overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="digest-panel">
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
            <div className="flex items-center gap-2">
              <Activity size={16} style={{ color: INFO }} />
              <p className="text-sm font-bold" style={{ color: '#1A1209' }}>Weekly Security Digest</p>
            </div>
            <div className="flex items-center gap-2">
              {digestSent && <span className="text-xs font-medium" style={{ color: SUCCESS }}>Sent!</span>}
              <button onClick={handleSendDigest} disabled={sendingDigest}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white flex items-center gap-1.5"
                style={{ backgroundColor: INFO }} data-testid="send-digest-btn">
                {sendingDigest ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                Send to Admins
              </button>
              <button onClick={() => setShowDigest(false)}><X size={14} style={{ color: MUTED }} /></button>
            </div>
          </div>
          <div className="p-1" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {digestHtml ? (
              <iframe title="Digest Preview" srcDoc={digestHtml} className="w-full border-0" style={{ height: 380 }} data-testid="digest-preview-iframe" />
            ) : (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin" size={20} style={{ color: BRAND }} /></div>
            )}
          </div>
        </div>
      )}

      {/* ── User Security Health Panel ── */}
      {showUserHealth && (
        <div className="bg-white rounded-lg mb-6 overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="user-health-panel">
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
            <div className="flex items-center gap-2">
              <Users size={16} style={{ color: DANGER }} />
              <p className="text-sm font-bold" style={{ color: '#1A1209' }}>User Security Health</p>
              {userHealth && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${DANGER}12`, color: DANGER }}>{userHealth.at_risk} at risk</span>}
            </div>
            <button onClick={() => setShowUserHealth(false)}><X size={14} style={{ color: MUTED }} /></button>
          </div>
          {loadingHealth ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin" size={20} style={{ color: BRAND }} /></div>
          ) : userHealth ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                    <th className="text-left px-4 py-2.5 font-medium text-xs" style={{ color: MUTED }}>User</th>
                    <th className="text-left px-4 py-2.5 font-medium text-xs" style={{ color: MUTED }}>Role</th>
                    <th className="text-left px-4 py-2.5 font-medium text-xs" style={{ color: MUTED }}>2FA</th>
                    <th className="text-left px-4 py-2.5 font-medium text-xs" style={{ color: MUTED }}>Risk</th>
                    <th className="text-left px-4 py-2.5 font-medium text-xs" style={{ color: MUTED }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {userHealth.users.slice(0, 10).map((u) => (
                    <tr key={u.user_id} className="border-b" style={{ borderColor: '#F0F0F0' }} data-testid={`user-health-${u.user_id}`}>
                      <td className="px-4 py-2.5">
                        <p className="text-xs font-medium" style={{ color: '#1A1209' }}>{u.name}</p>
                        <p className="text-[10px]" style={{ color: MUTED }}>{u.email}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded capitalize font-medium" style={{ backgroundColor: '#F5F5F5', color: MUTED }}>{u.role.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        {u.has_2fa
                          ? <CheckCircle size={14} style={{ color: SUCCESS }} />
                          : <XCircle size={14} style={{ color: DANGER }} />}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-[10px] px-2 py-0.5 rounded font-bold" style={{
                          backgroundColor: u.risk_level === 'critical' ? `${DANGER}15` : u.risk_level === 'high' ? '#FEF3C7' : `${SUCCESS}15`,
                          color: u.risk_level === 'critical' ? DANGER : u.risk_level === 'high' ? '#B45309' : SUCCESS,
                        }}>{u.risk_level.toUpperCase()} ({u.risk_score})</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {u.risk_factors.map((f, i) => (
                            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#FEF2F2', color: DANGER }}>{f}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}

      {/* ── SMS Alert Configuration Panel ── */}
      {showAlertConfig && (
        <div className="bg-white rounded-lg mb-6 overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="sms-alert-panel">
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
            <div className="flex items-center gap-2">
              <Phone size={16} style={{ color: SUCCESS }} />
              <p className="text-sm font-bold" style={{ color: '#1A1209' }}>SMS Security Alerts</p>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: alertEnabled ? `${SUCCESS}15` : '#F5F5F5', color: alertEnabled ? SUCCESS : MUTED }}>
                {alertEnabled ? 'ACTIVE' : 'OFF'}
              </span>
            </div>
            <button onClick={() => setShowAlertConfig(false)}><X size={14} style={{ color: MUTED }} /></button>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setAlertEnabled(!alertEnabled)} data-testid="alert-toggle">
                {alertEnabled ? <ToggleRight size={28} style={{ color: SUCCESS }} /> : <ToggleLeft size={28} style={{ color: MUTED }} />}
              </button>
              <span className="text-xs font-medium" style={{ color: '#1A1209' }}>Enable SMS alerts for security events</span>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider block mb-1.5" style={{ color: MUTED }}>Recipients (comma-separated phone numbers)</label>
              <input value={alertRecipients} onChange={e => setAlertRecipients(e.target.value)}
                placeholder="+1234567890, +0987654321"
                className="w-full px-3 py-2 rounded-lg text-xs border" style={{ borderColor: '#E0E0E0' }} data-testid="alert-recipients-input" />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider block mb-1.5" style={{ color: MUTED }}>Alert Types</label>
              <div className="flex flex-wrap gap-2">
                {['auto_block', 'brute_force', 'off_hours_admin', 'bulk_block', 'account_locked'].map(t => (
                  <button key={t} onClick={() => setAlertTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                    className="text-[10px] px-2.5 py-1 rounded-full font-medium border transition-colors"
                    style={{
                      backgroundColor: alertTypes.includes(t) ? `${BRAND}12` : '#fff',
                      borderColor: alertTypes.includes(t) ? BRAND : '#E0E0E0',
                      color: alertTypes.includes(t) ? BRAND : MUTED,
                    }} data-testid={`alert-type-${t}`}>
                    {t.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button onClick={handleSaveAlertConfig} disabled={savingAlert}
                className="px-4 py-2 rounded-lg text-xs font-medium text-white flex items-center gap-1.5"
                style={{ backgroundColor: SUCCESS }} data-testid="save-alert-config-btn">
                {savingAlert ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />} Save Config
              </button>
              <div className="flex items-center gap-1.5 flex-1">
                <input value={testPhone} onChange={e => setTestPhone(e.target.value)}
                  placeholder="Phone for test SMS" className="px-3 py-2 rounded-lg text-xs border flex-1" style={{ borderColor: '#E0E0E0' }} data-testid="test-phone-input" />
                <button onClick={handleTestAlert} disabled={testingAlert || !testPhone}
                  className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1" style={{ backgroundColor: `${INFO}12`, color: INFO }} data-testid="test-alert-btn">
                  {testingAlert ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Test
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Security Timeline Panel ── */}
      {showTimeline && timeline && (
        <div className="bg-white rounded-lg mb-6 overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="timeline-panel">
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
            <div className="flex items-center gap-2">
              <History size={16} style={{ color: BRAND }} />
              <p className="text-sm font-bold" style={{ color: '#1A1209' }}>Security Audit Timeline</p>
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5F5F5', color: MUTED }}>{timeline.length} events</span>
            </div>
            <button onClick={() => setShowTimeline(false)}><X size={14} style={{ color: MUTED }} /></button>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            <div className="relative pl-6">
              <div className="absolute left-2 top-0 bottom-0 w-px" style={{ backgroundColor: '#E0E0E0' }} />
              {timeline.map((ev, i) => {
                const actionIcons = {
                  login: <CheckCircle size={10} style={{ color: SUCCESS }} />,
                  login_failed: <XCircle size={10} style={{ color: DANGER }} />,
                  ip_blocked: <ShieldOff size={10} style={{ color: DANGER }} />,
                  ip_unblocked: <Unlock size={10} style={{ color: SUCCESS }} />,
                  jwt_key_rotated: <Key size={10} style={{ color: '#F59E0B' }} />,
                  password_changed: <Lock size={10} style={{ color: INFO }} />,
                  '2fa_verified': <Fingerprint size={10} style={{ color: SUCCESS }} />,
                  '2fa_failed': <Fingerprint size={10} style={{ color: DANGER }} />,
                  security_digest_sent: <Activity size={10} style={{ color: INFO }} />,
                };
                return (
                  <div key={i} className="relative pb-4 last:pb-0" data-testid={`timeline-event-${i}`}>
                    <div className="absolute -left-4 top-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-white border" style={{ borderColor: '#E0E0E0' }}>
                      {actionIcons[ev.action] || <Activity size={10} style={{ color: MUTED }} />}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: '#1A1209' }}>{formatAction(ev.action)}</span>
                        <span className="text-[9px] font-mono" style={{ color: MUTED }}>{ev.timestamp?.slice(0, 19).replace('T', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {ev.ip_address && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F5F5F5', color: MUTED }}>{ev.ip_address}</span>}
                        {ev.user_id && <span className="text-[9px]" style={{ color: MUTED }}>{ev.user_id.slice(0, 15)}...</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Locked Accounts (if any) ── */}
      {locked_accounts && locked_accounts.length > 0 && (
        <div className="bg-white rounded-lg mb-6 p-5" style={{ border: `1px solid ${DANGER}30` }} data-testid="locked-accounts">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Lock size={16} style={{ color: DANGER }} />
              <h2 className="text-sm font-bold" style={{ color: DANGER }}>Locked Accounts</h2>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${DANGER}12`, color: DANGER }}>{locked_accounts.length}</span>
            </div>
          </div>
          <div className="space-y-2">
            {locked_accounts.map((acct) => (
              <div key={acct.user_id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#FEF2F2' }} data-testid={`locked-${acct.user_id}`}>
                <div>
                  <p className="text-xs font-medium" style={{ color: '#1A1209' }}>{acct.first_name} {acct.last_name} <span style={{ color: MUTED }}>({acct.email})</span></p>
                  <p className="text-[10px]" style={{ color: MUTED }}>Locked: {acct.locked_at?.slice(0, 16).replace('T', ' ')} — {acct.lock_reason || 'No reason given'}</p>
                </div>
                <button onClick={() => handleUnlockAccount(acct.user_id)} disabled={unlockingUser === acct.user_id}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-medium flex items-center gap-1" style={{ backgroundColor: `${SUCCESS}15`, color: SUCCESS }} data-testid={`unlock-${acct.user_id}`}>
                  {unlockingUser === acct.user_id ? <Loader2 size={10} className="animate-spin" /> : <Unlock size={10} />} Unlock
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Security Score + Compliance ── */}
      {(sectionProps('score').visible || sectionProps('compliance').visible) && security_score && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-testid="score-compliance-row">
          {/* Security Score Gauge */}
          <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #E0E0E0' }} data-testid="security-score">
            <div className="flex items-center gap-2 mb-5">
              <Target size={18} style={{ color: BRAND }} />
              <h2 className="text-base font-bold" style={{ color: '#1A1209' }}>Security Score</h2>
            </div>
            <div className="flex items-center gap-8">
              {/* Score Ring */}
              <div className="relative w-32 h-32 flex-shrink-0">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#F0F0F0" strokeWidth="8" />
                  <circle cx="50" cy="50" r="42" fill="none"
                    stroke={security_score.score >= 75 ? SUCCESS : security_score.score >= 50 ? '#F59E0B' : DANGER}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${security_score.score * 2.64} 264`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold" style={{ color: security_score.score >= 75 ? SUCCESS : security_score.score >= 50 ? '#F59E0B' : DANGER }}>
                    {security_score.score}
                  </span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white mt-0.5"
                    style={{ backgroundColor: security_score.score >= 90 ? SUCCESS : security_score.score >= 75 ? '#16A34A' : security_score.score >= 60 ? '#F59E0B' : DANGER }}>
                    {security_score.grade}
                  </span>
                </div>
              </div>
              {/* Factor Breakdown */}
              <div className="flex-1 space-y-2">
                {security_score.factors.map((f) => (
                  <div key={f.name}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[11px] font-medium" style={{ color: '#1A1209' }}>{f.name}</span>
                      <span className="text-[10px] font-mono" style={{ color: MUTED }}>{f.score}/100 ({f.weight}%)</span>
                    </div>
                    <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${f.score}%`,
                        backgroundColor: f.score >= 70 ? SUCCESS : f.score >= 40 ? '#F59E0B' : DANGER,
                      }} />
                    </div>
                    <p className="text-[9px] mt-0.5" style={{ color: MUTED }}>{f.detail}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Score History Trend */}
            {security_score.history && security_score.history.length > 1 && (
              <div className="mt-5 pt-4 border-t" style={{ borderColor: '#F0F0F0' }} data-testid="score-history">
                <p className="text-[10px] font-medium uppercase tracking-wider mb-2" style={{ color: MUTED }}>Score Trend</p>
                <ResponsiveContainer width="100%" height={80}>
                  <LineChart data={security_score.history}>
                    <XAxis dataKey="date" tick={{ fontSize: 8, fill: MUTED }} tickFormatter={d => d.slice(5)} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 8, fill: MUTED }} width={20} />
                    <Tooltip contentStyle={{ fontSize: 10 }} formatter={(v) => [`${v}/100`, 'Score']} />
                    <Line type="monotone" dataKey="score" stroke={BRAND} strokeWidth={2} dot={{ fill: BRAND, r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Compliance Checklist */}
          {compliance && (
            <div className="bg-white rounded-lg p-6" style={{ border: '1px solid #E0E0E0' }} data-testid="compliance-checklist">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={18} style={{ color: BRAND }} />
                  <h2 className="text-base font-bold" style={{ color: '#1A1209' }}>SOC2 / ISO Compliance</h2>
                </div>
                <span className="text-sm font-bold px-3 py-1 rounded-full" style={{
                  backgroundColor: compliance.pct === 100 ? `${SUCCESS}15` : `${BRAND}15`,
                  color: compliance.pct === 100 ? SUCCESS : BRAND,
                }}>{compliance.pass_count}/{compliance.total_count} Passing</span>
              </div>
              <div className="space-y-2">
                {compliance.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 py-2 px-3 rounded-lg" style={{ backgroundColor: '#FAFAFA' }} data-testid={`compliance-${item.id}`}>
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{
                      backgroundColor: item.status === 'pass' ? `${SUCCESS}20` : item.status === 'warn' ? '#FEF3C7' : item.status === 'info' ? `${INFO}15` : `${DANGER}15`,
                    }}>
                      {item.status === 'pass' ? <CheckCircle size={12} style={{ color: SUCCESS }} /> :
                       item.status === 'warn' ? <AlertTriangle size={12} style={{ color: '#F59E0B' }} /> :
                       item.status === 'info' ? <Info size={12} style={{ color: INFO }} /> :
                       <XCircle size={12} style={{ color: DANGER }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium" style={{ color: '#1A1209' }}>{item.label}</p>
                      <p className="text-[10px]" style={{ color: MUTED }}>{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Threat Intelligence + Active Sessions ── */}
      {(sectionProps('threat').visible || sectionProps('sessions').visible) && threat_intel && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-testid="threat-sessions-row">
          {/* Hourly Attack Pattern */}
          <div className="bg-white rounded-lg p-5 lg:col-span-2" style={{ border: '1px solid #E0E0E0' }} data-testid="hourly-attacks">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap size={18} style={{ color: BRAND }} />
                <h2 className="text-base font-bold" style={{ color: '#1A1209' }}>Threat Intelligence</h2>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${DANGER}12`, color: DANGER }}>
                  {threat_intel.brute_force_ips} brute-force IP{threat_intel.brute_force_ips !== 1 ? 's' : ''}
                </span>
                <span className="text-[10px] px-2 py-1 rounded-full font-medium" style={{ backgroundColor: '#F5F5F5', color: MUTED }}>
                  {threat_intel.total_attack_ips} sources (7d)
                </span>
              </div>
            </div>
            {/* Two charts side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Hourly Heat */}
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider mb-2" style={{ color: MUTED }}>Attack Hours (7d)</p>
                {threat_intel.hourly_attacks.length > 0 ? (
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={
                      Array.from({ length: 24 }, (_, i) => {
                        const h = String(i).padStart(2, '0');
                        const found = threat_intel.hourly_attacks.find(a => a.hour === h);
                        return { hour: `${h}:00`, count: found ? found.count : 0 };
                      })
                    }>
                      <XAxis dataKey="hour" tick={{ fontSize: 8, fill: MUTED }} interval={5} />
                      <YAxis tick={{ fontSize: 9, fill: MUTED }} width={20} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      <Bar dataKey="count" name="Failed Logins" radius={[2, 2, 0, 0]}>
                        {Array.from({ length: 24 }, (_, i) => {
                          const h = String(i).padStart(2, '0');
                          const found = threat_intel.hourly_attacks.find(a => a.hour === h);
                          const count = found ? found.count : 0;
                          return <Cell key={i} fill={count >= 10 ? DANGER : count >= 3 ? '#F59E0B' : `${MUTED}40`} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-center py-8" style={{ color: MUTED }}>No attacks detected</p>
                )}
              </div>
              {/* Attack Velocity */}
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider mb-2" style={{ color: MUTED }}>Attack Velocity (7d)</p>
                {threat_intel.attack_velocity.length > 0 ? (
                  <ResponsiveContainer width="100%" height={140}>
                    <LineChart data={threat_intel.attack_velocity}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: MUTED }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fontSize: 9, fill: MUTED }} width={20} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="attacks" stroke={DANGER} strokeWidth={2} dot={{ fill: DANGER, r: 3 }} name="Attacks" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-center py-8" style={{ color: MUTED }}>No velocity data</p>
                )}
              </div>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="active-sessions">
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} style={{ color: BRAND }} />
              <h2 className="text-base font-bold" style={{ color: '#1A1209' }}>Active Sessions</h2>
            </div>
            <div className="text-center py-3 mb-4 rounded-lg" style={{ backgroundColor: '#FAFAFA' }}>
              <p className="text-3xl font-bold" style={{ color: '#1A1209' }}>{active_sessions?.total_24h || 0}</p>
              <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>Logins in last 24h</p>
            </div>
            {(active_sessions?.by_role || []).length > 0 ? (
              <div className="space-y-2">
                {active_sessions.by_role.map((r) => {
                  const roleColors = { admin: '#7B4FD8', corporate_admin: BRAND, school_admin: SUCCESS, parent: INFO, teen: '#EC4899', user: MUTED };
                  const color = roleColors[r.role] || MUTED;
                  return (
                    <div key={r.role} className="flex items-center gap-3" data-testid={`session-role-${r.role}`}>
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs flex-1 capitalize" style={{ color: '#1A1209' }}>{r.role.replace(/_/g, ' ')}</span>
                      <span className="text-xs font-bold" style={{ color }}>{r.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-center py-4" style={{ color: MUTED }}>No sessions in last 24h</p>
            )}
            <div className="mt-4 pt-3 border-t" style={{ borderColor: '#F0F0F0' }}>
              <div className="flex items-center gap-1.5">
                <TrendingUp size={12} style={{ color: SUCCESS }} />
                <span className="text-[10px]" style={{ color: MUTED }}>
                  {overview.login_success_30d + overview.login_fail_30d} total auth events (30d)
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Anomaly Detection + Recommendations ── */}
      {(sectionProps('anomalies').visible || sectionProps('recommendations').visible) && (anomalies || recommendations) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-testid="anomaly-recs-row">
          {/* Anomaly Detection */}
          {anomalies && (
            <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="anomaly-detection">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={18} style={{ color: anomalies.total_anomalies > 0 ? DANGER : SUCCESS }} />
                  <h2 className="text-base font-bold" style={{ color: '#1A1209' }}>Anomaly Detection</h2>
                </div>
                <span className="text-xs font-bold px-2 py-1 rounded-full" style={{
                  backgroundColor: anomalies.total_anomalies > 5 ? `${DANGER}12` : anomalies.total_anomalies > 0 ? '#FEF3C7' : `${SUCCESS}15`,
                  color: anomalies.total_anomalies > 5 ? DANGER : anomalies.total_anomalies > 0 ? '#B45309' : SUCCESS,
                }}>{anomalies.total_anomalies} detected</span>
              </div>
              <div className="space-y-3">
                {/* Off-hours */}
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: anomalies.off_hours_count > 0 ? '#FEF2F2' : '#FAFAFA' }}>
                  <Clock size={16} style={{ color: anomalies.off_hours_count > 0 ? DANGER : MUTED }} />
                  <div className="flex-1">
                    <p className="text-xs font-medium" style={{ color: '#1A1209' }}>Off-Hours Activity (11PM-5AM)</p>
                    <p className="text-[10px]" style={{ color: MUTED }}>{anomalies.off_hours_count} events in last 24h</p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: anomalies.off_hours_count > 0 ? DANGER : SUCCESS }}>{anomalies.off_hours_count}</span>
                </div>
                {/* New IPs */}
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: anomalies.new_ips.length > 0 ? '#FFF7ED' : '#FAFAFA' }}>
                  <Globe size={16} style={{ color: anomalies.new_ips.length > 0 ? '#F59E0B' : MUTED }} />
                  <div className="flex-1">
                    <p className="text-xs font-medium" style={{ color: '#1A1209' }}>New Attack Sources</p>
                    <p className="text-[10px]" style={{ color: MUTED }}>
                      {anomalies.new_ips.length > 0
                        ? anomalies.new_ips.map(ip => ip.ip).join(', ')
                        : 'No new IPs detected'}
                    </p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: anomalies.new_ips.length > 0 ? '#F59E0B' : SUCCESS }}>{anomalies.new_ips.length}</span>
                </div>
                {/* Rapid-fire */}
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: anomalies.rapid_fire.length > 0 ? '#FEF2F2' : '#FAFAFA' }}>
                  <Zap size={16} style={{ color: anomalies.rapid_fire.length > 0 ? DANGER : MUTED }} />
                  <div className="flex-1">
                    <p className="text-xs font-medium" style={{ color: '#1A1209' }}>Rapid-Fire Attacks</p>
                    <p className="text-[10px]" style={{ color: MUTED }}>
                      {anomalies.rapid_fire.length > 0
                        ? anomalies.rapid_fire.map(r => `${r.ip} (${r.count}x)`).join(', ')
                        : 'No rapid-fire patterns detected'}
                    </p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: anomalies.rapid_fire.length > 0 ? DANGER : SUCCESS }}>{anomalies.rapid_fire.length}</span>
                </div>
              </div>
            </div>
          )}

          {/* Security Recommendations */}
          {recommendations && (
            <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="security-recommendations">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} style={{ color: BRAND }} />
                <h2 className="text-base font-bold" style={{ color: '#1A1209' }}>Recommendations</h2>
              </div>
              {recommendations.length > 0 ? (
                <div className="space-y-2">
                  {recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: '#FAFAFA' }} data-testid={`rec-${i}`}>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                        backgroundColor: rec.priority === 'high' ? `${DANGER}15` : rec.priority === 'medium' ? '#FEF3C7' : `${INFO}15`,
                      }}>
                        {rec.priority === 'high' ? <AlertTriangle size={12} style={{ color: DANGER }} /> :
                         rec.priority === 'medium' ? <Info size={12} style={{ color: '#F59E0B' }} /> :
                         <CheckCircle size={12} style={{ color: INFO }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-medium" style={{ color: '#1A1209' }}>{rec.action}</p>
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-bold" style={{
                            backgroundColor: rec.priority === 'high' ? `${DANGER}12` : rec.priority === 'medium' ? '#FEF3C7' : `${INFO}12`,
                            color: rec.priority === 'high' ? DANGER : rec.priority === 'medium' ? '#B45309' : INFO,
                          }}>{rec.priority.toUpperCase()}</span>
                        </div>
                        <p className="text-[10px]" style={{ color: MUTED }}>{rec.detail}</p>
                        <p className="text-[9px] font-medium mt-0.5" style={{ color: SUCCESS }}>{rec.impact}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle size={24} className="mx-auto mb-2" style={{ color: SUCCESS }} />
                  <p className="text-xs font-medium" style={{ color: SUCCESS }}>All clear! No recommendations at this time.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Overview Metrics ── */}
      {sectionProps('overview').visible && (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="security-overview">
        <MetricCard icon={CheckCircle} label="Logins (30d)" value={overview.login_success_30d} color={SUCCESS} sub="Successful" />
        <MetricCard icon={AlertTriangle} label="Failed (30d)" value={overview.login_fail_30d} color={DANGER} accent={overview.login_fail_30d > 50 ? DANGER : undefined} sub={`${overview.fail_rate_pct}% fail rate`} />
        <MetricCard icon={Smartphone} label="2FA Verifications" value={overview.two_fa_verifications_30d} color={INFO} sub="Last 30 days" />
        <MetricCard icon={Key} label="Password Resets" value={overview.password_resets_30d} color="#F59E0B" sub="Requested" />
        <MetricCard icon={Lock} label="Password Changes" value={overview.password_changes_30d} color="#7B4FD8" sub="User-initiated" />
        <MetricCard icon={Shield} label="Active Keys" value={key_rotation.active_key_count} color={BRAND} sub={`Max ${key_rotation.max_retained_keys}`} />
      </div>
      )}

      {/* ── Period Comparison + Bulk Actions + Heatmap ── */}
      {(sectionProps('comparison').visible || sectionProps('heatmap').visible) && period_comparison && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6" data-testid="period-heatmap-row">
          {/* Period Comparison */}
          <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="period-comparison">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} style={{ color: BRAND }} />
              <h2 className="text-sm font-bold" style={{ color: '#1A1209' }}>This Week vs Last Week</h2>
            </div>
            <div className="space-y-3">
              {[
                { key: 'logins', label: 'Successful Logins', icon: <CheckCircle size={14} style={{ color: SUCCESS }} />, positive: true },
                { key: 'failures', label: 'Failed Attempts', icon: <AlertTriangle size={14} style={{ color: DANGER }} />, positive: false },
                { key: 'blocks', label: 'IPs Blocked', icon: <ShieldOff size={14} style={{ color: '#F59E0B' }} />, positive: false },
              ].map(({ key, label, icon, positive }) => {
                const d = period_comparison[key] || {};
                const isUp = d.delta > 0;
                const isGood = positive ? isUp : !isUp;
                return (
                  <div key={key} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: '#FAFAFA' }} data-testid={`delta-${key}`}>
                    {icon}
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium" style={{ color: '#1A1209' }}>{label}</p>
                      <p className="text-[10px]" style={{ color: MUTED }}>{d.current} this week • {d.previous} last week</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {d.delta !== 0 && (isUp
                        ? <ArrowUpRight size={14} style={{ color: isGood ? SUCCESS : DANGER }} />
                        : <ArrowDownRight size={14} style={{ color: isGood ? SUCCESS : DANGER }} />
                      )}
                      <span className="text-xs font-bold" style={{ color: d.delta === 0 ? MUTED : isGood ? SUCCESS : DANGER }}>
                        {d.delta > 0 ? '+' : ''}{d.delta}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Bulk Action */}
            <div className="mt-4 pt-3 border-t" style={{ borderColor: '#F0F0F0' }}>
              <button onClick={handleBulkBlock} disabled={bulkBlocking}
                className="w-full px-3 py-2 rounded-lg text-[10px] font-medium flex items-center justify-center gap-1.5 border transition-colors"
                style={{ borderColor: DANGER, color: DANGER, backgroundColor: `${DANGER}08` }}
                data-testid="bulk-block-btn">
                {bulkBlocking ? <Loader2 size={12} className="animate-spin" /> : <ShieldOff size={12} />}
                Block All Flagged IPs (5+ attempts)
              </button>
            </div>
          </div>

          {/* Failed Login Heatmap */}
          <div className="bg-white rounded-lg p-5 lg:col-span-2" style={{ border: '1px solid #E0E0E0' }} data-testid="login-heatmap">
            <div className="flex items-center gap-2 mb-4">
              <Zap size={16} style={{ color: DANGER }} />
              <h2 className="text-sm font-bold" style={{ color: '#1A1209' }}>Attack Heatmap (30d)</h2>
              <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F5F5F5', color: MUTED }}>Day × Hour</span>
            </div>
            {heatmap && heatmap.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="inline-grid gap-px" style={{ gridTemplateColumns: 'auto repeat(24, 1fr)' }}>
                  {/* Hour headers */}
                  <div />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} className="text-center text-[8px] pb-1 font-mono" style={{ color: MUTED, minWidth: 20 }}>{String(h).padStart(2, '0')}</div>
                  ))}
                  {/* Day rows */}
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, dayIdx) => (
                    <React.Fragment key={day}>
                      <div className="text-[9px] font-medium pr-2 flex items-center" style={{ color: MUTED }}>{day}</div>
                      {Array.from({ length: 24 }, (_, hour) => {
                        const cell = heatmap.find(c => c.day_idx === dayIdx && c.hour === hour);
                        const count = cell ? cell.count : 0;
                        const maxCount = Math.max(...heatmap.map(c => c.count), 1);
                        const intensity = count / maxCount;
                        return (
                          <div key={hour} title={`${day} ${String(hour).padStart(2, '0')}:00 — ${count} failed`}
                            className="rounded-sm cursor-pointer"
                            style={{
                              width: 20, height: 16,
                              backgroundColor: count === 0 ? '#F5F5F5' : `rgba(220, 38, 38, ${0.15 + intensity * 0.85})`,
                            }}
                            data-testid={`heatmap-${dayIdx}-${hour}`}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-[8px]" style={{ color: MUTED }}>Less</span>
                  {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
                    <div key={i} className="w-3 h-3 rounded-sm" style={{ backgroundColor: v === 0 ? '#F5F5F5' : `rgba(220, 38, 38, ${0.15 + v * 0.85})` }} />
                  ))}
                  <span className="text-[8px]" style={{ color: MUTED }}>More</span>
                </div>
              </div>
            ) : (
              <p className="text-xs text-center py-8" style={{ color: MUTED }}>No attack data for heatmap</p>
            )}
          </div>
        </div>
      )}

      {/* ── Login Trend Chart ── */}
      <SectionHeader icon={Activity} title="Login Activity — Last 7 Days" />
      <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="login-trend-chart">
        {login_trend.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={login_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: MUTED }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: MUTED }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="success" stroke={SUCCESS} fill={`${SUCCESS}30`} strokeWidth={2} name="Successful" />
              <Area type="monotone" dataKey="failed" stroke={DANGER} fill={`${DANGER}30`} strokeWidth={2} name="Failed" />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-center py-10" style={{ color: MUTED }}>No login data in the last 7 days</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* ── 2FA Adoption ── */}
        <div>
          <SectionHeader icon={Fingerprint} title="2FA Adoption" />
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="2fa-adoption">
            <div className="flex items-center gap-6">
              <div className="w-32 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={twoFaPieData} dataKey="value" cx="50%" cy="50%" outerRadius={55} innerRadius={35} paddingAngle={2}>
                      {twoFaPieData.map((_, i) => <Cell key={i} fill={[INFO, '#7B4FD8', '#E5E7EB'][i]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} users`, n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{two_fa_adoption.adoption_pct}%</p>
                  <p className="text-xs" style={{ color: MUTED }}>adoption rate</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: INFO }} />SMS: {two_fa_adoption.sms_2fa}</p>
                  <p className="text-xs"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: '#7B4FD8' }} />Email: {two_fa_adoption.email_2fa}</p>
                  <p className="text-xs"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: '#E5E7EB' }} />None: {two_fa_adoption.total_users - two_fa_adoption.any_2fa}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t flex items-center gap-4" style={{ borderColor: '#E0E0E0' }}>
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={14} style={{ color: SUCCESS }} />
                <span className="text-xs" style={{ color: MUTED }}>Trusted devices: <strong>{trusted_devices.active}</strong> active</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock size={14} style={{ color: MUTED }} />
                <span className="text-xs" style={{ color: MUTED }}>Expired: {trusted_devices.expired}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Event Breakdown ── */}
        <div>
          <SectionHeader icon={Zap} title="Security Events — Last 24h" />
          <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="event-breakdown">
            {event_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={event_breakdown.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: MUTED }} />
                  <YAxis type="category" dataKey="action" width={130} tick={{ fontSize: 10, fill: MUTED }} tickFormatter={a => formatAction(a).slice(0, 18)} />
                  <Tooltip formatter={(v) => [v, 'Events']} labelFormatter={formatAction} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {event_breakdown.slice(0, 8).map((entry, i) => (
                      <Cell key={i} fill={severityColor(entry.action)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-center py-10" style={{ color: MUTED }}>No security events in the last 24 hours</p>
            )}
          </div>
        </div>
      </div>

      {/* ── Flagged IPs ── */}
      <SectionHeader icon={Globe} title="Flagged IPs — Top Failed Login Sources (7d)" />
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="flagged-ips">
        {flagged_ips.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>IP Address</th>
                <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Failed Attempts</th>
                <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Last Attempt</th>
                <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Status</th>
                <th className="text-right px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {flagged_ips.map((ip, i) => (
                <tr key={i} className="border-b" style={{ borderColor: '#F0F0F0' }}>
                  <td className="px-4 py-3 font-mono text-xs">
                    {ip.ip}
                    {whitelistSet.has(ip.ip) && <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-blue-50 text-blue-500 font-medium">SAFE</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: ip.attempts >= 10 ? DANGER : ip.attempts >= 5 ? '#F59E0B' : MUTED }}>
                      {ip.attempts}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: MUTED }}>{ip.last_attempt ? new Date(ip.last_attempt).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium" style={{ color: ip.attempts >= 10 ? DANGER : ip.attempts >= 5 ? '#F59E0B' : SUCCESS }}>
                      {ip.attempts >= 10 ? 'High Risk' : ip.attempts >= 5 ? 'Medium' : 'Low'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {blockedSet.has(ip.ip) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold text-white" style={{ backgroundColor: DANGER }}>
                        <ShieldAlert size={10} /> BLOCKED
                      </span>
                    ) : (
                      <button onClick={() => handleBlockIp(ip.ip, `Flagged: ${ip.attempts} failed logins`)} disabled={blockingIp === ip.ip}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium transition-colors"
                        style={{ backgroundColor: `${DANGER}12`, color: DANGER }} data-testid={`block-ip-${ip.ip}`}>
                        {blockingIp === ip.ip ? <Loader2 size={10} className="animate-spin" /> : <ShieldAlert size={10} />} Block
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-center py-8" style={{ color: MUTED }}>No flagged IPs in the last 7 days</p>
        )}
      </div>

      {/* ── Country Signup Blocking ── */}
      <SectionHeader icon={Globe} title={`Country Signup Blocking (${(geoBlock.countries || []).length})`} />
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="geo-block-section">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
          <div>
            <p className="text-sm font-medium" style={{ color: '#1A1209' }}>Block new signups from selected countries</p>
            <p className="text-[11px]" style={{ color: MUTED }}>Existing accounts keep working — only new registrations are declined. Detected via Cloudflare IP country.</p>
          </div>
          <button onClick={handleGeoBlockToggle} data-testid="geo-block-toggle" className="flex items-center gap-1.5 text-sm font-medium" style={{ color: geoBlock.enabled ? SUCCESS : MUTED }}>
            {geoBlock.enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
            {geoBlock.enabled ? 'ON' : 'OFF'}
          </button>
        </div>
        <div className="flex items-center gap-2 p-4 border-b" style={{ borderColor: '#E0E0E0' }}>
          <input type="text" value={geoBlockInput} onChange={(e) => setGeoBlockInput(e.target.value.toUpperCase())} maxLength={2}
            placeholder="ISO code e.g. RU" className="w-40 px-3 py-2 text-sm rounded-lg border outline-none font-mono"
            style={{ borderColor: '#E0E0E0' }} data-testid="geo-block-input" />
          <button onClick={handleGeoBlockAdd} disabled={geoBlockInput.trim().length !== 2 || geoBlockSaving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: DANGER }} data-testid="geo-block-add-btn">
            {geoBlockSaving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Block country
          </button>
        </div>
        <div className="p-4 flex flex-wrap gap-2">
          {(geoBlock.countries || []).length > 0 ? geoBlock.countries.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold font-mono" style={{ backgroundColor: `${DANGER}12`, color: DANGER }} data-testid={`geo-block-chip-${c}`}>
              {c} ({GEO_NAMES[c] || c})
              <button onClick={() => handleGeoBlockRemove(c)} data-testid={`geo-block-remove-${c}`}><X size={12} /></button>
            </span>
          )) : <p className="text-sm" style={{ color: MUTED }}>No countries blocked</p>}
        </div>
      </div>

      {/* ── Blocked IPs ── */}
      <SectionHeader icon={ShieldAlert} title={`Blocked IPs (${(blocked_ips || []).length})`} />
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="blocked-ips-section">
        <div className="flex items-center gap-2 p-4 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
          <input type="text" value={manualBlockIp} onChange={(e) => setManualBlockIp(e.target.value)}
            placeholder="Enter IP to block..." className="flex-1 px-3 py-2 text-sm rounded-lg border outline-none"
            style={{ borderColor: '#E0E0E0' }} data-testid="manual-block-input" />
          <button onClick={() => { if (manualBlockIp.trim()) { handleBlockIp(manualBlockIp.trim()); setManualBlockIp(''); } }}
            disabled={!manualBlockIp.trim() || blockingIp} className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: DANGER }} data-testid="manual-block-btn">
            {blockingIp ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />} Block
          </button>
        </div>
        {(blocked_ips || []).length > 0 ? (
          <div className="divide-y" style={{ borderColor: '#F0F0F0' }}>
            {blocked_ips.map((b, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`blocked-ip-${b.ip}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${DANGER}12` }}>
                  <ShieldAlert size={14} style={{ color: DANGER }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-mono font-medium" style={{ color: '#1A1209' }}>{b.ip}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold" style={{
                      backgroundColor: b.type === 'auto' ? `${BRAND}15` : `${DANGER}12`,
                      color: b.type === 'auto' ? BRAND : DANGER,
                    }}>{b.type === 'auto' ? 'AUTO' : 'MANUAL'}</span>
                  </div>
                  <p className="text-[10px]" style={{ color: MUTED }}>
                    {b.reason} — {b.blocked_at ? new Date(b.blocked_at).toLocaleString() : ''}
                    {b.expires_at && <span style={{ color: BRAND }}> — Expires: {new Date(b.expires_at).toLocaleString()}</span>}
                    {!b.expires_at && <span style={{ color: DANGER }}> — Permanent</span>}
                  </p>
                </div>
                <button onClick={() => handleUnblockIp(b.ip)} disabled={unblockingIp === b.ip}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-medium border flex items-center gap-1" style={{ borderColor: '#E0E0E0', color: SUCCESS }}
                  data-testid={`unblock-ip-${b.ip}`}>
                  {unblockingIp === b.ip ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle size={10} />} Unblock
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-center py-6" style={{ color: MUTED }}>No IPs currently blocked</p>
        )}
      </div>

      {/* ── IP Whitelist ── */}
      <SectionHeader icon={ShieldCheck} title={`IP Whitelist (${(whitelisted_ips || []).length})`} />
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="ip-whitelist">
        <div className="flex items-center gap-2 p-4 border-b" style={{ borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }}>
          <input type="text" value={whitelistIp} onChange={(e) => setWhitelistIp(e.target.value)}
            placeholder="IP address..." className="w-40 px-3 py-2 text-sm rounded-lg border outline-none"
            style={{ borderColor: '#E0E0E0' }} data-testid="whitelist-ip-input" />
          <input type="text" value={whitelistLabel} onChange={(e) => setWhitelistLabel(e.target.value)}
            placeholder="Label (e.g. Office, VPN)..." className="flex-1 px-3 py-2 text-sm rounded-lg border outline-none"
            style={{ borderColor: '#E0E0E0' }} data-testid="whitelist-label-input" />
          <button onClick={handleAddWhitelist} disabled={!whitelistIp.trim() || addingWhitelist}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: SUCCESS }} data-testid="add-whitelist-btn">
            {addingWhitelist ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
          </button>
        </div>
        {(whitelisted_ips || []).length > 0 ? (
          <div className="divide-y" style={{ borderColor: '#F0F0F0' }}>
            {whitelisted_ips.map((w, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`wl-ip-${w.ip}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${SUCCESS}12` }}>
                  <ShieldCheck size={14} style={{ color: SUCCESS }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono font-medium" style={{ color: '#1A1209' }}>{w.ip}</p>
                  <p className="text-[10px]" style={{ color: MUTED }}>{w.label || 'No label'} — Added {w.added_at ? new Date(w.added_at).toLocaleString() : ''}</p>
                </div>
                <button onClick={() => handleRemoveWhitelist(w.ip)} disabled={removingWl === w.ip}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-medium border flex items-center gap-1" style={{ borderColor: '#E0E0E0', color: DANGER }}
                  data-testid={`remove-wl-${w.ip}`}>
                  {removingWl === w.ip ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />} Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-center py-6" style={{ color: MUTED }}>No whitelisted IPs — Add trusted IPs like office or VPN addresses</p>
        )}
      </div>

      {/* ── Key Rotation ── */}
      <SectionHeader icon={Key} title="JWT Key Rotation" />
      <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="key-rotation">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium" style={{ color: '#1A1209' }}>{key_rotation.active_key_count} active key{key_rotation.active_key_count !== 1 ? 's' : ''}</p>
            <p className="text-xs" style={{ color: MUTED }}>Max {key_rotation.max_retained_keys} retained for grace period</p>
          </div>
          {!rotateConfirm ? (
            <button onClick={() => setRotateConfirm(true)} className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5" style={{ backgroundColor: `${BRAND}15`, color: BRAND }} data-testid="rotate-key-btn">
              <RefreshCw size={14} /> Rotate Key
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: DANGER }}>Confirm?</span>
              <button onClick={handleRotateKey} disabled={rotating} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white flex items-center gap-1" style={{ backgroundColor: DANGER }} data-testid="confirm-rotate-btn">
                {rotating ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />} {rotating ? 'Rotating...' : 'Yes, Rotate'}
              </button>
              <button onClick={() => setRotateConfirm(false)} className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: '#E0E0E0' }}>Cancel</button>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {key_rotation.keys.map((k, i) => (
            <div key={k.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: i === 0 ? `${SUCCESS}08` : '#FAFAFA' }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: i === 0 ? `${SUCCESS}20` : '#E5E7EB' }}>
                {i === 0 ? <ShieldCheck size={12} style={{ color: SUCCESS }} /> : <Clock size={12} style={{ color: MUTED }} />}
              </div>
              <div className="flex-1">
                <p className="text-xs font-mono font-medium" style={{ color: '#1A1209' }}>
                  Key: {k.id} {i === 0 && <span className="text-[10px] font-sans px-1.5 py-0.5 rounded ml-1 text-white" style={{ backgroundColor: SUCCESS }}>PRIMARY</span>}
                </p>
                <p className="text-[10px]" style={{ color: MUTED }}>Activated: {new Date(k.activated_at).toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
        {key_rotation.history.length > 0 && (
          <div className="mt-4 pt-3 border-t" style={{ borderColor: '#E0E0E0' }}>
            <p className="text-xs font-medium mb-2" style={{ color: MUTED }}>Rotation History</p>
            {key_rotation.history.slice(0, 5).map((h, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] py-1" style={{ color: MUTED }}>
                <RefreshCw size={10} />
                <span>{new Date(h.timestamp).toLocaleString()}</span>
                <span>by {h.user_id?.slice(0, 12)}...</span>
                {h.ip_address && <span className="font-mono">from {h.ip_address}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Rate Limiting ── */}
      <SectionHeader icon={ShieldAlert} title="Rate Limiting Configuration" />
      <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="rate-limits">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Endpoint</th>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Max Requests</th>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Window</th>
              <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: MUTED }}>Type</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(rate_limits).map(([route, config]) => (
              <tr key={route} className="border-b" style={{ borderColor: '#F0F0F0' }}>
                <td className="px-4 py-2.5 font-mono text-xs">{route}</td>
                <td className="px-4 py-2.5">
                  <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: `${BRAND}15`, color: BRAND }}>{config.max}</span>
                </td>
                <td className="px-4 py-2.5 text-xs" style={{ color: MUTED }}>{config.window >= 60 ? `${config.window / 60} min` : `${config.window}s`}</td>
                <td className="px-4 py-2.5">
                  <span className="text-xs" style={{ color: route.includes('login') || route.includes('2fa') ? DANGER : '#F59E0B' }}>
                    {route.includes('login') || route.includes('2fa') ? 'Auth Critical' : 'Standard'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Recent Events Toggle ── */}
      <div className="mt-8">
        <button onClick={() => setShowEvents(!showEvents)} className="flex items-center gap-2 text-sm font-medium" style={{ color: BRAND }} data-testid="toggle-events-btn">
          <Eye size={14} /> {showEvents ? 'Hide' : 'Show'} Recent Security Events ({recent_events.length})
          {showEvents ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showEvents && (
          <div className="mt-3 bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="recent-events">
            <div className="max-h-96 overflow-y-auto">
              {recent_events.length > 0 ? recent_events.map((evt, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: '#F5F5F5' }}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: severityColor(evt.action) }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: '#1A1209' }}>{formatAction(evt.action)}</p>
                    <p className="text-[10px] truncate" style={{ color: MUTED }}>{evt.user_id?.slice(0, 16)}... {evt.ip_address && `| ${evt.ip_address}`}</p>
                  </div>
                  <span className="text-[10px] flex-shrink-0" style={{ color: MUTED }}>{evt.timestamp ? new Date(evt.timestamp).toLocaleTimeString() : '—'}</span>
                </div>
              )) : (
                <p className="text-sm text-center py-8" style={{ color: MUTED }}>No security events in the last 24 hours</p>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSecurity;
